import { useAuth } from 'wasp/client/auth';
import { useMemo } from 'react';

export interface User {
  id: string;
  email: string;
  emailVerified: boolean;
  authProvider: string;
  onboardingComplete: boolean;
  isAdmin?: boolean;
  subscriptionTier?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UseUserReturn {
  // Core auth state
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;

  // Helper functions
  hasRole: (role: string) => boolean;
  isEmailVerified: boolean;
  isAdmin: boolean;
  subscriptionTier: string | null;

  // Common checks
  canAccessAdmin: boolean;
  needsOnboarding: boolean;
  isDemoUser: boolean;
}

/**
 * Custom hook that provides consistent user data access with proper error handling and loading states.
 * Wraps Wasp's useAuth with additional utilities for common user checks and role-based access.
 */
export function useUser(): UseUserReturn {
  const { data: user, isLoading, error } = useAuth();

  const userData = useMemo(() => {
    if (!user) return null;

    return {
      id: user.id,
      email: user.email || '',
      emailVerified: user.emailVerified || false,
      authProvider: user.authProvider || 'email',
      onboardingComplete: user.onboardingComplete || false,
      isAdmin: user.isAdmin || false,
      subscriptionTier: user.subscriptionTier || undefined,
      firstName: (user as any).firstName || undefined,
      lastName: (user as any).lastName || undefined,
      displayName: (user as any).displayName || undefined,
      createdAt: (user as any).createdAt || undefined,
      updatedAt: (user as any).updatedAt || undefined,
    };
  }, [user]);

  const isAuthenticated = !!userData;
  const isEmailVerified = userData?.emailVerified || false;
  const isAdmin = userData?.isAdmin || false;
  const subscriptionTier = userData?.subscriptionTier || null;

  // Helper functions
  const hasRole = (role: string): boolean => {
    if (!userData) return false;

    switch (role.toLowerCase()) {
      case 'admin':
        return userData.isAdmin === true;
      case 'user':
        return isAuthenticated;
      default:
        return false;
    }
  };

  // Common checks
  const canAccessAdmin = hasRole('admin');
  const needsOnboarding = userData ? !userData.onboardingComplete : false;
  const isDemoUser = subscriptionTier === 'demo';

  return {
    // Core auth state
    user: userData,
    isAuthenticated,
    isLoading,
    error: error as Error | null,

    // Helper functions
    hasRole,
    isEmailVerified,
    isAdmin,
    subscriptionTier,

    // Common checks
    canAccessAdmin,
    needsOnboarding,
    isDemoUser,
  };
}